// Vista del operador: cuánto se le debe pagar a cada repartidor en un rango. Usa el
// MISMO cálculo que ve el repartidor en la app, así el total por día coincide exacto.
//
// Rediseño ago-2026 ("colas de trabajo"): la vista se organiza alrededor del trabajo
// del operador — cerrar la semana y pagarle a la flota sin pagar sin respaldo — y no
// alrededor de las features. De arriba abajo:
//
//   1. Encabezado   → semana (dom–sáb) + LA acción ("Pagar semana") + menú ⋯ con todo
//                     lo secundario (extras, lotes, exportaciones, notificación, rango).
//   2. Cinta        → total del período + barra Pagado / Listo para pagar / Bloqueado
//                     + "Te falta" (validar / pagar / resolver) que activa su pestaña.
//   3. Lista        → pestañas por TAREA (no filtros sueltos) + buscador. La fila es un
//                     resumen; todo el detalle y las acciones viven en la ficha.
//   4. Ficha        → documento con conciliación declarado vs calculado, días con
//                     switch de pago + "Gestionar", datos bancarios (del summary).
//   5. Pagar semana → asistente: elegibles / excluidos con motivo → planilla banco →
//                     confirmar (marca pagados los días pendientes de los elegibles).
//
// Regla de diseño: cero botones por fila y una sola acción primaria por pantalla.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  Landmark,
  Layers,
  Mail,
  MoreHorizontal,
  PackageCheck,
  Pencil,
  RotateCcw,
  Search,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Upload,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import {
  getDriverPayrollSummary,
  markDriverDayPaid,
  markDriverDaysPaidBulk,
  markDriverDaysPaidSelection,
  setDriverPaymentDate,
  listDriverExtrasBatches,
  getDriverExtrasBatchItems,
  rollbackDriverExtrasBatch,
  getDriverPaymentBankExport,
  notifyDriverPayrollAmounts,
  rejectDriverBillingDocument,
  uploadDriverBillingDocument,
  getAiValidationStatus,
  listAiValidations,
  rollbackAiValidation,
} from '../../../api/billing';
import { Avatar } from '../../../components/ui/Avatar';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { CopyButton } from '../../../components/ui/CopyButton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Switch } from '../../../components/ui/Switch';
import { Select } from '../../../components/ui/Select';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { cn } from '../../../utils/cn';
import useDriverDirectory from '../../../hooks/useDriverDirectory';
import useAuthScope from '../../../hooks/useAuthScope';
import { useCountry } from '../../../context/CountryContext';
import { getActiveDeliveryUsers } from '../../../api/user';
import { driverVehicleLabel } from '../../../utils/driverVehicleTypes';
import { billingDocLabel, docTypeMismatchLabel } from '../../../utils/billingDocTypes';
import { formatCurrency, formatDate } from './utils/cobroHelpers';
import { selectBankSheetRows } from './utils/bankSheetRows';
import { downloadBankSheetFor, formatForBank, SUPPORTED_BANK_CODES } from './utils/bankFormats';
import { CHILE_BANKS } from '../../../utils/chileBanks';
import {
  currentWeek,
  dateTimeLabel,
  dayLabel,
  daysAfterClose,
  isFullWeek,
  isWeekStillOpen,
  previousWeek,
  shiftWeeks,
  todayYMD,
  weekLabel,
  weekOf,
} from './utils/payrollWeeks';
import { driverPendingAmount, pendingSummary } from './utils/driverFilters';
import {
  QUEUE_ALL,
  classifyDriver,
  docComparableInRange,
  docDelta,
  docExpectedTotal,
  docExpectedTotals,
} from './utils/driverQueues';
import { downloadPayrollSummarySheet } from './utils/payrollSummarySheet';
import { downloadExtrasBatchSheet } from './utils/extrasBatchSheet';
// Estilos .bill-page: antes los importaba CobroView (cuando esta vista era su
// pestaña); como ruta propia debe traerlos ella misma.
import '../billing/billing.scss';
import DayPaymentModal from './DayPaymentModal';
import BulkExtrasModal from './BulkExtrasModal';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';
import AcceptDocumentModal from './AcceptDocumentModal';
import AiValidationModal, { REASON_DISABLED, scansLabel } from './AiValidationModal';

const getTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/Santiago';
  }
};

const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const DAY_STATUS_TONE = { paid: 'success', partial: 'warning', unpaid: 'neutral' };
const DAY_STATUS_LABEL = { paid: 'Pagado', partial: 'Parcial', unpaid: 'Sin pagar' };

// Estado del documento del período, como badge del DS (un solo vocabulario).
const DocBadge = ({ d, weekOpen, t }) => {
  if (d.isFreelancer) return <Badge tone="neutral">{t('Freelancer · sin documento')}</Badge>;
  if (weekOpen) return <Badge tone="neutral">{t('Semana en curso')}</Badge>;
  switch (d.billingDocStatus) {
    case 'accepted':
      return <Badge tone="success" dot>{t('Aceptado')}</Badge>;
    case 'uploaded':
      return <Badge tone="info" dot>{t('Por validar')}</Badge>;
    case 'rejected':
      return <Badge tone="error" dot>{t('Rechazado')}</Badge>;
    default:
      return <Badge tone="warning" dot>{t('Falta documento')}</Badge>;
  }
};

// Aviso discreto cuando el tipo con que se paga no es el del perfil actual (o el rango
// mezcla semanas de tipos distintos). Informativo: no bloquea el pago. `title` en vez de
// Tooltip porque la fila entera es un <button>; la ficha repite la explicación visible.
const DocTypeMismatchBadge = ({ d, t }) => {
  const info = docTypeMismatchLabel(d, t);
  if (!info) return null;
  return (
    <Badge tone="muted" title={info.title} className="max-w-full min-w-0">
      <span className="truncate">{info.label}</span>
    </Badge>
  );
};

// Avance del pago de la fila: días pagados + saldo pendiente en plata.
// `className` lo usa la fila para reacomodar la celda en mobile (ver la lista).
const PayProgress = ({ d, pending, t, className }) => {
  const pct = d.daysCount > 0 ? (d.paidDaysCount / d.daysCount) * 100 : 0;
  return (
    <span className={cn('flex flex-col gap-1 min-w-0', className)}>
      <span className="flex items-center gap-2 text-xs text-fg-muted whitespace-nowrap">
        <span
          className="w-16 h-1.5 rounded-full bg-bg-subtle overflow-hidden shrink-0"
          role="progressbar"
          aria-valuenow={d.paidDaysCount}
          aria-valuemin={0}
          aria-valuemax={d.daysCount}
          aria-label={t('Días pagados')}
        >
          <span
            className="block h-full rounded-full bg-status-success transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </span>
        <span className="tabular-nums">
          {t('{{p}}/{{n}} días', { p: d.paidDaysCount, n: d.daysCount })}
        </span>
      </span>
      {pending > 0 ? (
        <span className="text-xs font-medium text-status-warning tabular-nums">
          {t('{{monto}} pendiente', { monto: formatCurrency(pending) })}
        </span>
      ) : (
        <span className="text-xs text-status-success">{t('Pagado completo')}</span>
      )}
    </span>
  );
};

// Sub-línea "N sobredim." bajo el conteo de entregas: restaura la visibilidad de los
// sobredimensionados (pago diferenciado) que el rediseño ago-2026 dejó de mostrar.
const OversizedLine = ({ n, t }) =>
  Number(n ?? 0) > 0 ? (
    <span className="block text-[11px] text-fg-subtle tabular-nums whitespace-nowrap">
      {t('{{n}} sobredim.', { n: Number(n) })}
    </span>
  ) : null;

// Sub-línea "N retiros" (retiros realizados en recolecciones), mismo trato que
// OversizedLine. Con `net` suma el monto (lista de días de la ficha); sin él, solo la
// cantidad (fila de la lista). Campo ausente (billing sin desplegar) = sin retiros.
const PickupsLine = ({ n, net, t }) => {
  const count = Number(n ?? 0);
  if (count <= 0) return null;
  return (
    <span className="block text-[11px] text-fg-subtle tabular-nums whitespace-nowrap">
      {net == null
        ? t('{{n}} retiros', { n: count })
        : t('{{n}} retiros · {{monto}}', { n: count, monto: formatCurrency(net) })}
    </span>
  );
};

// Total que transfiere una planilla: la suma de sus filas (lo que el banco ejecuta), que
// no es el total "Elegibles" de la vista: un día con monto retenido no se transfiere.
const bankSheetTotal = (rows) =>
  (rows ?? []).reduce((sum, r) => sum + (Number(r?.amount) || 0), 0);

// Directorio del operador: correo de la CUENTA y datos bancarios por repartidor. Lo usan
// el resumen exportado (correo + cruce emisor/titular) y la planilla del banco (columna
// "Correo beneficiario" con el correo de la cuenta). Se cachea por operador: el roster
// completo (hasta 10.000 usuarios con sus datos) es una llamada pesada y no cambia dentro
// de la sesión. Si el catálogo falla devuelve mapas vacíos: la exportación no se bloquea
// (el correo de la cuenta queda sin llenar, no se rellena con otro).
const contactsCache = new Map(); // logisticCompanyId -> { bankByDriver, emailByDriver }

async function fetchDriverContacts(logisticCompanyId) {
  const cacheKey = String(logisticCompanyId ?? '__all__');
  const cached = contactsCache.get(cacheKey);
  if (cached) return cached;

  const bankByDriver = new Map();
  const emailByDriver = new Map();
  try {
    const res = await getActiveDeliveryUsers({ logisticCompanyId });
    // `getActiveDeliveryUsers` NO lanza: devuelve { success: false } si la llamada falló.
    const docs =
      res && res.success !== false && !res.error
        ? Array.isArray(res.data)
          ? res.data
          : res?.data?.docs || res?.data?.users || res?.data?.data || []
        : [];
    for (const u of docs) {
      const id = u?._id || u?.id;
      if (!id) continue;
      if (u?.bankData) bankByDriver.set(String(id), u.bankData);
      if (u?.email) emailByDriver.set(String(id), u.email);
    }
  } catch {
    // Sin catálogo: correo y banco vacíos, no se bloquea la exportación.
  }
  const contacts = { bankByDriver, emailByDriver };
  contactsCache.set(cacheKey, contacts);
  return contacts;
}

const DriverPaymentsSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getName } = useDriverDirectory();
  const { logisticCompanyId } = useAuthScope();
  const { countryCode } = useCountry();
  // El cruce RUT emisor/titular es chileno (módulo 11 + RUT como identificación): fuera de
  // CL ese mismo campo lleva CUIT/NIT/cédula y el cruce no bloquea el pago.
  const issuerMustMatch = countryCode === 'CL';

  // Por defecto, la semana anterior completa (dom–sáb): es lo que el operador liquida.
  const initial = useMemo(() => previousWeek(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState(QUEUE_ALL);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Rango personalizado (vive en el menú ⋯, plegado del flujo principal).
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(initial.from);
  const [draftTo, setDraftTo] = useState(initial.to);

  // Ficha: guarda el ID para leer siempre el driver fresco tras cada refetch.
  const [fichaId, setFichaId] = useState(null);
  const [dayBusy, setDayBusy] = useState(''); // date del switch en curso
  const [bulkBusy, setBulkBusy] = useState(false);
  // Fechas de pago (negocio, 'YYYY-MM-DD'): la del footer "Marcar período pagado" y la
  // edición inline de un día ya pagado.
  const [fichaPayDate, setFichaPayDate] = useState(todayYMD());
  const [editPayDay, setEditPayDay] = useState(null); // { date, value }
  const [editPayBusy, setEditPayBusy] = useState(false);
  const [dayModal, setDayModal] = useState(null); // { driverId, driverName, date, isFreelancer }
  const [viewerDoc, setViewerDoc] = useState(null); // { doc, driverName }
  const [acceptTarget, setAcceptTarget] = useState(null); // { driverId, driverName, doc, d, expected }
  const [rejectTarget, setRejectTarget] = useState(null); // { driverId, driverName, weekStart, weekEnd }
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  // Subida admin del documento en nombre del repartidor (para cuando el documento
  // existe pero el repartidor no puede subirlo). El backend no dispara correo.
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadType, setUploadType] = useState('boleta');
  const uploadInputRef = useRef(null);

  // Asistente "Pagar semana".
  const [payOpen, setPayOpen] = useState(false);
  const [payDone, setPayDone] = useState(null); // { updated } tras confirmar
  const [payBusy, setPayBusy] = useState(false);
  const [payDate, setPayDate] = useState(todayYMD()); // fecha de emisión del pago
  const [bankBusy, setBankBusy] = useState(false);
  const [missing, setMissing] = useState(null); // excluidos de la planilla (bank-export)
  // Paso inline de selección de banco (solo operadores de Chile): { origin, rows, missing }.
  const [bankPick, setBankPick] = useState(null);
  const [selectedBank, setSelectedBank] = useState('');

  // Menú ⋯: lotes, carga masiva, notificación, resumen.
  const [showBulk, setShowBulk] = useState(false);
  const [lotesOpen, setLotesOpen] = useState(false);
  const [batches, setBatches] = useState(null);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchDlBusy, setBatchDlBusy] = useState('');
  const [rollbackBusy, setRollbackBusy] = useState('');
  const [exportBusy, setExportBusy] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyPreview, setNotifyPreview] = useState(null);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyFailed, setNotifyFailed] = useState(null);

  // Validación con IA. `aiStatus` dice si la función existe para este operador; un
  // backend sin el endpoint (404/501) o caído se lee como deshabilitada, sin avisar:
  // el botón simplemente no aparece. `aiModal` abre el asistente en la etapa indicada.
  const [aiStatus, setAiStatus] = useState(null); // { enabled, mode, activeJob, ... }
  const [aiModal, setAiModal] = useState(null); // { stage: 'preview'|'progress'|'result', jobId? }
  const [aiHistoryOpen, setAiHistoryOpen] = useState(false);
  const [aiJobs, setAiJobs] = useState(null);
  const [aiJobsLoading, setAiJobsLoading] = useState(false);
  const [aiRollbackBusy, setAiRollbackBusy] = useState('');

  const fetchData = useCallback(async (f, tt) => {
    setLoading(true);
    try {
      const res = await getDriverPayrollSummary({ from: f, to: tt, tz: getTz() });
      setData(res?.data ?? null);
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo cargar la liquidación'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData(initial.from, initial.to);
  }, [fetchData, initial.from, initial.to]);

  const loadAiStatus = useCallback(async () => {
    try {
      const res = await getAiValidationStatus();
      setAiStatus(
        res?.data && typeof res.data === 'object'
          ? res.data
          : { enabled: false, unavailable: true },
      );
    } catch {
      // 404/501/red: el backend no tiene la función todavía. Ahí sí se oculta todo: no hay
      // nada que "esté próximo", simplemente no existe en este entorno.
      setAiStatus({ enabled: false, unavailable: true });
    }
  }, []);

  useEffect(() => {
    loadAiStatus();
  }, [loadAiStatus]);

  // Al cambiar de ficha, la fecha de pago vuelve a hoy y se cierra la edición inline.
  useEffect(() => {
    setFichaPayDate(todayYMD());
    setEditPayDay(null);
  }, [fichaId]);

  // Cada apertura del asistente parte con la fecha de emisión en hoy.
  useEffect(() => {
    if (payOpen) setPayDate(todayYMD());
  }, [payOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const applyRange = useCallback((range) => {
    setFrom(range.from);
    setTo(range.to);
    setDraftFrom(range.from);
    setDraftTo(range.to);
    setTab(QUEUE_ALL);
    setSearch('');
    // La planilla ya generada es de OTRO rango: se descarta (el archivo lleva las filas
    // del rango viejo y el nombre del nuevo).
    setBankPick(null);
    fetchData(range.from, range.to);
  }, [fetchData]);

  const rangeIsWeek = isFullWeek(from, to);
  const weekOpen = isWeekStillOpen(from);
  const shownWeek = rangeIsWeek ? { from, to } : weekOf(from);
  const atCurrent = shownWeek.from === currentWeek().from;

  // ---------- derivados ----------

  const allDrivers = useMemo(() => data?.drivers ?? [], [data]);
  const totals = data?.totals;
  // Retiros realizados: 'unavailable' = billing necesitaba los retiros y pickups no
  // respondió, así que los totales del período NO los incluyen (hay que decirlo). Sin el
  // campo (billing sin desplegar) o con 'disabled'/'ok' no hay nada que advertir.
  const pickupsUnavailable = data?.pickupsStatus === 'unavailable';
  const pending = useMemo(() => pendingSummary(allDrivers), [allDrivers]);

  // Clasificación por cola + conciliación, calculadas una vez por driver.
  const rows = useMemo(
    () =>
      allDrivers.map((d) => ({
        d,
        // El catálogo del directorio solo trae usuarios ACTIVOS: para un repartidor
        // bloqueado cae al nombre que resuelve el propio summary (users por _id).
        name: getName(d.driverId) || d.driverName || d.driverId,
        ...classifyDriver(d, { weekOpen, t, issuerMustMatch }),
        delta: docDelta(d, { rangeIsWeek }),
      })),
    [allDrivers, getName, weekOpen, t, rangeIsWeek, issuerMustMatch],
  );

  const byQueue = useMemo(() => {
    const m = { validar: [], pagar: [], observacion: [], pagado: [] };
    for (const r of rows) if (m[r.queue]) m[r.queue].push(r);
    return m;
  }, [rows]);

  // Qué manda a validar la IA: cada documento 'uploaded' de la cola "Por validar" (un
  // rango libre puede traer más de una semana por repartidor; el job trabaja por
  // (driverId, weekStart), no por repartidor).
  const aiItems = useMemo(
    () =>
      byQueue.validar.flatMap((r) =>
        (r.d.billingDocuments ?? [])
          .filter((doc) => doc.status === 'uploaded' && doc.weekStart)
          .map((doc) => ({ driverId: r.d.driverId, weekStart: doc.weekStart })),
      ),
    [byQueue.validar],
  );
  const aiEnabled = !!aiStatus?.enabled;
  const aiActiveJob = aiStatus?.activeJob ?? null;
  // El botón se muestra siempre que haya cola y el backend conozca la función; si aún no está
  // configurada para este operador, va deshabilitado con el motivo, para que se sepa que está
  // próxima y no que falta algo en la vista.
  const aiKnown = !!aiStatus && !aiStatus.unavailable;
  // Activa: solo con cola (no hay nada que validar si no). "Próximamente": en toda semana
  // cerrada, para que se sepa que la función existe aunque hoy no haya pendientes.
  const showAiButton =
    aiKnown && !weekOpen && (byQueue.validar.length > 0 || !aiEnabled);
  const aiDisabledReason = !aiEnabled && aiStatus?.reasonDisabled
    ? t(REASON_DISABLED[aiStatus.reasonDisabled] ?? aiStatus.reasonDisabled)
    : !aiEnabled ? t('La IA no está configurada para este operador') : null;

  const readyAmount = byQueue.pagar.reduce((s, r) => s + r.pending, 0);
  const paidAmount = totals ? (totals.totalPayable ?? 0) - pending.amount : 0;
  const blockedAmount = pending.amount - readyAmount;

  const visible = useMemo(() => {
    let list = tab === QUEUE_ALL ? rows : rows.filter((r) => r.queue === tab);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => r.name.toLowerCase().includes(q) || r.d.driverId.includes(q));
    return list;
  }, [rows, tab, search]);

  const ficha = fichaId ? rows.find((r) => r.d.driverId === fichaId) ?? null : null;

  // ---------- acciones ----------

  const refetch = () => fetchData(from, to);

  const toggleDayPaid = async (driverId, date, paidNow) => {
    setDayBusy(date);
    try {
      await markDriverDayPaid({ driverId, date, paid: !paidNow, tz: getTz() });
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo actualizar el día'));
    } finally {
      setDayBusy('');
    }
  };

  const markWeekPaid = async (r) => {
    // Solo los días pendientes: mandar fecha explícita sobre días ya pagados los
    // sobrescribiría (la fecha explícita ES sobrescritura intencional en el backend).
    const dates = (r.d.days ?? [])
      .filter((day) => day.status === 'unpaid')
      .map((day) => day.date);
    if (dates.length === 0) {
      toast.info(t('No hay días pendientes en el período'));
      return;
    }
    setBulkBusy(true);
    try {
      await markDriverDaysPaidBulk({
        driverId: r.d.driverId,
        dates,
        paid: true,
        paymentDate: fichaPayDate,
        tz: getTz(),
      });
      toast.success(t('Semana de {{name}} marcada como pagada', { name: r.name }));
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo marcar pagado'));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-elegir el mismo archivo tras un error
    if (!file || !ficha) return;
    if (file.type !== 'application/pdf' && !String(file.name ?? '').toLowerCase().endsWith('.pdf')) {
      toast.error(t('El documento debe ser un PDF'));
      return;
    }
    setUploadBusy(true);
    try {
      await uploadDriverBillingDocument({
        driverId: ficha.d.driverId,
        date: shownWeek.from,
        docType: uploadType,
        file,
        tz: getTz(),
      });
      toast.success(t('Documento subido a nombre de {{name}}', { name: ficha.name }));
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo subir el documento'));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleRejectDoc = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    setRejectBusy(true);
    try {
      await rejectDriverBillingDocument({
        driverId: rejectTarget.driverId,
        weekStart: rejectTarget.weekStart,
        reason: rejectReason.trim(),
      });
      toast.success(t('Documento rechazado. Se le avisó al repartidor.'));
      setRejectTarget(null);
      setRejectReason('');
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo rechazar el documento'));
    } finally {
      setRejectBusy(false);
    }
  };

  // Confirmación del asistente: marca pagados TODOS los días pendientes de los
  // elegibles, en una sola llamada (mismo endpoint de la selección múltiple de antes).
  const confirmWeekPay = async () => {
    const items = [];
    for (const r of byQueue.pagar) {
      for (const day of r.d.days ?? []) {
        if (day.status === 'unpaid') items.push({ driverId: r.d.driverId, date: day.date });
      }
    }
    if (items.length === 0) return;
    setPayBusy(true);
    try {
      const res = await markDriverDaysPaidSelection({
        items,
        paid: true,
        paymentDate: payDate,
        tz: getTz(),
      });
      setPayDone({ updated: res?.data?.updated ?? items.length, drivers: byQueue.pagar.length });
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo registrar el pago'));
    } finally {
      setPayBusy(false);
    }
  };

  // Corrige la fecha de pago de UN día ya pagado (edición retroactiva, no cambia estado).
  const saveDayPaymentDate = async () => {
    if (!ficha || !editPayDay?.value) return;
    setEditPayBusy(true);
    try {
      await setDriverPaymentDate({
        paymentDate: editPayDay.value,
        items: [{ driverId: ficha.d.driverId, dates: [editPayDay.date] }],
      });
      toast.success(t('Fecha de pago actualizada'));
      setEditPayDay(null);
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo actualizar la fecha de pago'));
    } finally {
      setEditPayBusy(false);
    }
  };

  const handleBankExport = async () => {
    setBankBusy(true);
    try {
      const res = await getDriverPaymentBankExport({ from, to, tz: getTz() });
      const { origin = null, rows: bankRows = [], missing: miss = [], format = null } =
        res?.data ?? {};
      if (!origin) {
        toast.warning(t('Configura la cuenta origen en Ajustes → Pago a repartidores'));
        return;
      }
      // El backend arma sus filas sin mirar el ESTADO del documento (solo que esté
      // elegido): se dejan SOLO los elegibles de la vista —documento aceptado, cuenta a
      // nombre del emisor y datos bancarios completos— para que la planilla transfiera
      // exactamente lo que muestra "Elegibles". A los otros los lista el asistente.
      const { emailByDriver } = await fetchDriverContacts(logisticCompanyId);
      const eligibleIds = new Set(byQueue.pagar.map((r) => String(r.d.driverId)));
      const rows = selectBankSheetRows({
        rows: bankRows,
        eligibleIds,
        getEmail: (id) => emailByDriver.get(String(id)),
      });
      if (!rows.length) {
        toast.info(
          bankRows.length
            ? t('Ningún elegible entra a la planilla del banco')
            : t('No hay pagos pendientes en el período'),
        );
        if (miss.length) setMissing(miss);
        return;
      }
      // Sin formato (operador no chileno o backend viejo): planilla Santander como siempre.
      if (!format) {
        downloadBankSheetFor({
          code: '037',
          origin,
          rows,
          filename: formatForBank('037').filename(from, to),
        });
        toast.success(
          t('Planilla banco: {{n}} transferencias por {{monto}}', {
            n: rows.length,
            monto: formatCurrency(bankSheetTotal(rows)),
          }),
        );
        if (miss.length) setMissing(miss);
        return;
      }
      // Chile: paso inline para elegir el banco (preseleccionado con el del operador).
      setSelectedBank(format.bankCode);
      setBankPick({ origin, rows, missing: miss });
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo generar la planilla de banco'));
    } finally {
      setBankBusy(false);
    }
  };

  // Total que transfiere la planilla: es la suma de las filas (lo que el banco ejecuta),
  // no la del "Elegibles" de la vista: un día con monto retenido no se transfiere.
  const bankPickTotal = bankSheetTotal(bankPick?.rows);

  // Descarga la planilla con el banco elegido en el paso inline.
  const downloadPickedBank = () => {
    const fmt = formatForBank(selectedBank);
    if (!fmt || !bankPick) return;
    downloadBankSheetFor({
      code: selectedBank,
      origin: bankPick.origin,
      rows: bankPick.rows,
      filename: fmt.filename(from, to),
    });
    if (bankPick.missing.length) setMissing(bankPick.missing);
    setBankPick(null);
  };

  // Exporta el resumen CON datos bancarios y correo (cruce emisor/titular en el archivo).
  const handleExportSummary = async () => {
    setExportBusy(true);
    try {
      const { bankByDriver, emailByDriver } = await fetchDriverContacts(logisticCompanyId);
      // Mismo fallback que las filas: el directorio no incluye bloqueados.
      const nameForSheet = (id) =>
        getName(id) ||
        allDrivers.find((d) => String(d.driverId) === String(id))?.driverName ||
        undefined;
      downloadPayrollSummarySheet({
        drivers: allDrivers,
        totals,
        from,
        to,
        weekOpen,
        // 'unavailable' deja vacías las celdas de retiros: un 0 afirmaría que no hubo.
        pickupsStatus: data?.pickupsStatus,
        getName: nameForSheet,
        getEmail: (id) => emailByDriver.get(String(id)) ?? '',
        getBank: (id) => bankByDriver.get(String(id)) ?? null,
        t,
      });
    } finally {
      setExportBusy(false);
    }
  };

  const handleNotifyPreview = async () => {
    setNotifyBusy(true);
    try {
      const res = await notifyDriverPayrollAmounts({ from, to, tz: getTz(), dryRun: true });
      const recipients = res?.data?.recipients ?? [];
      if (recipients.length === 0) {
        toast.info(t('No hay repartidores con monto pendiente en el período'));
        return;
      }
      setNotifyPreview({ recipients });
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo notificar el monto a pago'));
    } finally {
      setNotifyBusy(false);
    }
  };

  const handleNotifySend = async () => {
    setNotifySending(true);
    try {
      const res = await notifyDriverPayrollAmounts({ from, to, tz: getTz() });
      const { sent = 0, failed = [] } = res?.data ?? {};
      setNotifyPreview(null);
      toast.success(t('{{sent}} correos enviados', { sent }));
      if (failed.length) setNotifyFailed(failed);
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo notificar el monto a pago'));
    } finally {
      setNotifySending(false);
    }
  };

  const recipientAmount = (r) => {
    // Semanas de tipos distintos: cada semana se paga con su tipo; los dos montos se
    // rotulan como referencia, sin elegir uno.
    if (r.mixedDocTypes === true) {
      return t('Factura {{fac}} · Boleta {{bol}} (según semana)', {
        fac: formatCurrency(r.factura?.payAmount),
        bol: formatCurrency(r.boleta?.payAmount),
      });
    }
    if (r.billingDocType === 'factura') return formatCurrency(r.factura?.payAmount);
    if (r.billingDocType === 'boleta') return formatCurrency(r.boleta?.payAmount);
    return t('Factura {{fac}} · Boleta {{bol}}', {
      fac: formatCurrency(r.factura?.payAmount),
      bol: formatCurrency(r.boleta?.payAmount),
    });
  };

  const openLotes = () => {
    setLotesOpen(true);
    if (batches === null) loadBatches();
  };

  const loadBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const res = await listDriverExtrasBatches();
      setBatches(res?.data ?? []);
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudieron cargar los lotes'));
      setBatches([]);
    } finally {
      setBatchesLoading(false);
    }
  }, [t]);

  const downloadBatch = async (b) => {
    setBatchDlBusy(b.batchId);
    try {
      const res = await getDriverExtrasBatchItems(b.batchId);
      const { batch = b, items = [] } = res?.data ?? {};
      if (items.length === 0) {
        toast.info(t('El lote no tiene filas para exportar'));
        return;
      }
      downloadExtrasBatchSheet({ batch, items, t });
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo descargar el lote'));
    } finally {
      setBatchDlBusy('');
    }
  };

  const rollback = async (batchId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('¿Revertir este lote? Se eliminarán todos sus extras.'))) return;
    setRollbackBusy(batchId);
    try {
      await rollbackDriverExtrasBatch(batchId);
      toast.success(t('Lote revertido'));
      await loadBatches();
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo revertir el lote'));
    } finally {
      setRollbackBusy('');
    }
  };

  // ---------- validación con IA ----------

  const openAiValidation = () => {
    // Con un job activo se retoma ESE en progreso; si no, parte por la vista previa.
    if (aiActiveJob?.jobId) setAiModal({ stage: 'progress', jobId: aiActiveJob.jobId });
    else setAiModal({ stage: 'preview' });
  };

  const loadAiJobs = useCallback(async () => {
    setAiJobsLoading(true);
    try {
      const res = await listAiValidations({ limit: 20 });
      setAiJobs(res?.data?.jobs ?? []);
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudieron cargar las validaciones'));
      setAiJobs([]);
    } finally {
      setAiJobsLoading(false);
    }
  }, [t]);

  // Se recarga en cada apertura: a diferencia de los lotes de extras, un job cambia solo
  // en el servidor (en cola → corriendo → listo) y otro operador puede haber lanzado uno.
  const openAiHistory = () => {
    setAiHistoryOpen(true);
    loadAiJobs();
  };

  // Al cerrar el asistente se relee el estado (job activo o no); si terminó, también la
  // liquidación (documentos que la IA aceptó) y el historial ya cargado.
  const onAiModalClose = () => {
    setAiModal(null);
    loadAiStatus();
  };
  const onAiFinished = () => {
    refetch();
    if (aiJobs !== null) loadAiJobs();
  };

  const rollbackAi = async (job) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('¿Deshacer este lote? Los documentos que aceptó la IA vuelven a "Por validar". Los ya pagados no se tocan.'))) return;
    setAiRollbackBusy(job.jobId);
    try {
      const res = await rollbackAiValidation(job.jobId);
      const { reverted = 0, blocked = 0 } = res?.data ?? {};
      toast.success(t('{{n}} aceptación(es) revertida(s)', { n: reverted }));
      if (blocked > 0) toast.warning(t('{{n}} no se revirtieron: ya estaban pagadas', { n: blocked }));
      await loadAiJobs();
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo deshacer el lote'));
    } finally {
      setAiRollbackBusy('');
    }
  };

  // ---------- piezas de UI ----------

  const MENU_ITEMS = [
    { icon: Upload, label: t('Carga masiva de extras'), fn: () => setShowBulk(true) },
    { icon: Layers, label: t('Lotes de extras'), fn: openLotes },
    ...(aiEnabled ? [{ icon: Sparkles, label: t('Validaciones con IA'), fn: openAiHistory }] : []),
    { icon: Mail, label: t('Notificar montos por correo'), fn: handleNotifyPreview },
    // Toda la configuración vive en Configuración de empresa: acá solo el atajo.
    { icon: Settings, label: t('Configuración'), fn: () => navigate('/admin/settings?tab=driver-payment-notifications') },
    { icon: Download, label: t('Descargar resumen (.xlsx)'), fn: handleExportSummary },
    { icon: SlidersHorizontal, label: t('Rango personalizado'), fn: () => setRangeOpen(true) },
  ];

  const TABS = weekOpen
    ? [{ key: QUEUE_ALL, label: t('Todos'), count: rows.length }]
    : [
        { key: QUEUE_ALL, label: t('Todos'), count: rows.length },
        { key: 'validar', label: t('Por validar'), count: byQueue.validar.length },
        { key: 'pagar', label: t('Listos para pagar'), count: byQueue.pagar.length },
        { key: 'observacion', label: t('Con observaciones'), count: byQueue.observacion.length },
        { key: 'pagado', label: t('Pagados'), count: byQueue.pagado.length },
      ];

  const TE_FALTA = [
    ['validar', t('Validar {{n}} documento(s)', { n: byQueue.validar.length }), byQueue.validar.length],
    ['pagar', t('Pagar a {{n}} repartidor(es)', { n: byQueue.pagar.length }), byQueue.pagar.length],
    ['observacion', t('Resolver {{n}} observación(es)', { n: byQueue.observacion.length }), byQueue.observacion.length],
  ].filter(([, , n]) => n > 0);

  const fichaDocs = ficha?.d.billingDocuments ?? [];

  // `w-full` en la raíz no es decorativo: el padre (`.content-main-container`) es un flex
  // de columna con `align-items: center`, así que un hijo sin ancho definido se
  // dimensiona a su min-content. Con la lista de repartidores de 760px eso daba 810px de
  // ancho de página en un viewport de 390px y toda la vista quedaba cortada en mobile.
  return (
    <div className="bill-page bill-page--lg w-full min-w-0">
      {/* `flex-wrap` hasta `xl`: el racimo de acciones (semana + IA + Pagar semana + ⋯)
          mide ~600px y, al ser `shrink-0`, en tablet asfixiaba el título hasta dejarlo
          en "…". Envolviendo, las acciones bajan a su propia línea y el título conserva
          el ancho completo. Se mantiene hasta `xl` y no hasta `lg` porque entre 1024 y
          1279 con el menú lateral desplegado tampoco caben en una fila. */}
      <PageHeader
        className="mb-6 flex-wrap xl:flex-nowrap"
        icon={<Wallet className="w-6 h-6 text-fg-muted" />}
        title={t('Pago a repartidores')}
        subtitle={t('La liquidación del período, igual a lo que cada repartidor ve en su app.')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Selector de semana dom–sáb */}
            <div className="inline-flex items-stretch rounded-lg border border-border shadow-soft overflow-hidden bg-bg">
              <button
                type="button"
                aria-label={t('Semana anterior')}
                disabled={loading}
                onClick={() => applyRange(shiftWeeks(shownWeek, -1))}
                className="flex items-center justify-center w-9 text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
              <span className="inline-flex items-center gap-2 px-3.5 h-9 text-sm font-medium text-fg whitespace-nowrap border-x border-border tabular-nums">
                <Calendar className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
                {weekLabel(shownWeek.from, shownWeek.to)}
              </span>
              <button
                type="button"
                aria-label={t('Semana siguiente')}
                disabled={loading || atCurrent}
                onClick={() => applyRange(shiftWeeks(shownWeek, 1))}
                className="flex items-center justify-center w-9 text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            </div>

            {/* Validación con IA: secundaria y solo cuando hay cola y la función existe.
                Con un job en curso se retoma ese (no se arranca otro). */}
            {showAiButton && !aiEnabled && (
              <Button
                variant="outline"
                iconLeft={<Sparkles className="w-4 h-4" />}
                disabled
                title={t('Disponible pronto: {{reason}}', { reason: aiDisabledReason })}
              >
                {t('Validar con IA · próximamente')}
              </Button>
            )}
            {showAiButton && aiEnabled && (
              <Button
                variant="outline"
                iconLeft={<Sparkles className="w-4 h-4" />}
                disabled={loading}
                onClick={openAiValidation}
                title={[
                  aiActiveJob
                    ? t('Hay una validación en curso: ver su avance.')
                    : aiStatus?.mode === 'shadow'
                      ? t('Modo sombra: simula qué aceptaría sin cambiar documentos.')
                      : null,
                  // Informativo: cómo se tratan los escaneos (PDF sin capa de texto).
                  scansLabel(aiStatus?.scans, t),
                ].filter(Boolean).join('\n') || undefined}
              >
                {aiActiveJob
                  ? t('Validando…')
                  : aiStatus?.mode === 'shadow' ? t('Simular con IA') : t('Validar con IA')}
              </Button>
            )}

            {/* LA acción de la vista */}
            <Button
              iconLeft={<Banknote className="w-4 h-4" />}
              disabled={loading || weekOpen || byQueue.pagar.length === 0}
              title={weekOpen
                ? t('La semana aún no cierra: los montos siguen cambiando.')
                : byQueue.pagar.length === 0
                  ? t('No hay repartidores listos para pagar.')
                  : undefined}
              onClick={() => { setPayDone(null); setPayOpen(true); }}
            >
              {t('Pagar semana')}
            </Button>

            {/* Todo lo secundario en un solo menú */}
            <div className="relative" ref={menuRef}>
              <Button
                variant="outline"
                size="icon"
                aria-label={t('Más acciones')}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <MoreHorizontal className="w-4 h-4" aria-hidden />
              </Button>
              {menuOpen && (
                <div className="absolute right-0 top-11 z-40 w-64 rounded-xl border border-border bg-bg shadow-elevated py-1.5">
                  {MENU_ITEMS.map(({ icon: Icon, label, fn }) => (
                    <button
                      key={label}
                      type="button"
                      className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-fg hover:bg-bg-muted transition-colors text-left"
                      onClick={() => { setMenuOpen(false); fn(); }}
                    >
                      <Icon className="w-4 h-4 text-fg-muted shrink-0" aria-hidden />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Retiros que billing no pudo leer: la cifra de abajo está incompleta y el operador
          tiene que saberlo antes de pagar con ella. */}
      {pickupsUnavailable && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-lg bg-status-warning-soft px-3.5 py-2.5 text-sm text-status-warning"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
          <span>
            {t('No se pudieron cargar los retiros realizados: los totales del período no los incluyen. Reintenta en unos minutos.')}
          </span>
        </div>
      )}

      {/* ===== Cinta del período: cuánto es, cuánto falta y POR QUÉ ===== */}
      <section className="rounded-xl border border-border bg-bg shadow-card p-4 sm:p-5 mb-6">
        {loading && !totals ? (
          <div className="flex flex-col gap-3" role="status" aria-busy="true">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-2.5 w-full" />
            <Skeleton className="h-4 w-full max-w-96" />
          </div>
        ) : totals ? (
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-3xl font-semibold text-fg tabular-nums">
                  {formatCurrency(totals.totalPayable)}
                </span>
                <span className="text-sm text-fg-muted">
                  {rangeIsWeek ? t('total de la semana') : t('total del período')}
                </span>
                {weekOpen && rangeIsWeek && (
                  <Badge tone="info" dot>{t('Semana en curso · los montos siguen cambiando')}</Badge>
                )}
                {!rangeIsWeek && (
                  <Badge tone="neutral">
                    {t('Rango personalizado: {{from}} al {{to}}', { from, to })}
                  </Badge>
                )}
              </div>

              {/* La plata contada de a tres: pagado / listo / bloqueado */}
              <div
                className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-bg-subtle"
                role="img"
                aria-label={t('Pagado {{p}}, listo para pagar {{l}}, bloqueado {{b}}', {
                  p: formatCurrency(paidAmount),
                  l: formatCurrency(readyAmount),
                  b: formatCurrency(blockedAmount),
                })}
              >
                {totals.totalPayable > 0 && paidAmount > 0 && (
                  <span
                    className="h-full bg-status-success"
                    style={{ width: `${(paidAmount / totals.totalPayable) * 100}%` }}
                  />
                )}
                {totals.totalPayable > 0 && readyAmount > 0 && (
                  <span
                    className="h-full bg-brand-500"
                    style={{ width: `${(readyAmount / totals.totalPayable) * 100}%` }}
                  />
                )}
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-status-success" aria-hidden />
                  <span className="text-fg-muted">{t('Pagado')}</span>
                  <span className="font-medium text-fg tabular-nums">{formatCurrency(paidAmount)}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-brand-500" aria-hidden />
                  <span className="text-fg-muted">{t('Listo para pagar')}</span>
                  <span className="font-medium text-fg tabular-nums">{formatCurrency(readyAmount)}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-bg-subtle border border-border" aria-hidden />
                  <span className="text-fg-muted">{t('Bloqueado')}</span>
                  <span className="font-medium text-fg tabular-nums">{formatCurrency(blockedAmount)}</span>
                </span>
              </div>

              <p className="mt-3 text-xs text-fg-subtle">
                {t('{{n}} entregas', { n: Number(totals.deliveriesCount ?? 0).toLocaleString('es-CL') })}
                {Number(totals.oversizedCount ?? 0) > 0 && (
                  <>
                    {' · '}{t('Sobredimensionados')}{' '}
                    <span className="font-medium text-fg tabular-nums">
                      {Number(totals.oversizedCount).toLocaleString('es-CL')}
                    </span>
                  </>
                )}
                {Number(totals.pickupsCount ?? 0) > 0 && (
                  <>
                    {' · '}
                    <span className="tabular-nums">
                      {t('{{n}} retiros · {{monto}}', {
                        n: Number(totals.pickupsCount).toLocaleString('es-CL'),
                        monto: formatCurrency(totals.pickupsNet ?? 0),
                      })}
                    </span>
                    {/* Retiros cuya parada no tiene monto: cuentan pero suman $0. */}
                    {Number(totals.pickupsUnpricedCount ?? 0) > 0 && (
                      <span className="text-status-warning">
                        {' '}({t('{{n}} sin tarifa', { n: Number(totals.pickupsUnpricedCount).toLocaleString('es-CL') })})
                      </span>
                    )}
                  </>
                )}
                {' · '}{t('Extras')}{' '}
                <span className={(totals.extrasTotal ?? 0) >= 0 ? 'text-status-success' : 'text-status-error'}>
                  {(totals.extrasTotal ?? 0) > 0 ? '+' : ''}{formatCurrency(totals.extrasTotal ?? 0)}
                </span>
                {' · '}{t('IVA y retenciones')} {formatCurrency(totals.taxAmount)}
                {' · '}{t('{{n}} repartidores', { n: allDrivers.length })}
              </p>
            </div>

            {/* Cola de trabajo: qué falta HACER. En mobile cae debajo del total y el
                separador pasa de borde izquierdo (columna) a borde superior (apilado). */}
            {!weekOpen && (
              <div className="lg:w-72 shrink-0 border-t border-border pt-3 lg:border-t-0 lg:pt-0 lg:border-l lg:border-border lg:pl-5 flex flex-col gap-1">
                <span className="text-xs font-medium text-fg-muted mb-1">{t('Te falta')}</span>
                {TE_FALTA.length === 0 ? (
                  <span className="inline-flex items-center gap-2 text-sm text-status-success py-1.5">
                    <CheckCircle className="w-4 h-4" aria-hidden />
                    {t('Período al día: nada pendiente.')}
                  </span>
                ) : (
                  TE_FALTA.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTab(key)}
                      className={cn(
                        // `py-3` en mobile: el enlace de la cola es el atajo de triage
                        // táctil, necesita el piso de 44px que da text-sm + py-3.
                        // En mobile el enlace va a sangre (sin padding lateral): así el
                        // texto alinea con el rótulo "Te falta" y el blanco táctil es más
                        // ancho. Desde `lg` vuelve al padding + margen negativo de la columna.
                        'group flex items-center justify-between gap-2 rounded-lg px-0 py-3 lg:px-2.5 lg:py-1.5 lg:-mx-2.5 text-sm text-left transition-colors',
                        'hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                        tab === key ? 'text-brand-700 font-medium' : 'text-fg',
                      )}
                    >
                      {label}
                      <ArrowRight
                        className="w-3.5 h-3.5 text-fg-subtle group-hover:text-fg-muted group-hover:translate-x-0.5 transition-transform"
                        aria-hidden
                      />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            icon={<Wallet />}
            title={t('Sin movimientos en el período')}
            description={t('Ningún repartidor registró entregas ni retiros en el rango seleccionado.')}
          />
        )}
      </section>

      {/* ===== Lista: pestañas por tarea + buscador ===== */}
      <section className="rounded-xl border border-border bg-bg shadow-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 pt-2 border-b border-border">
          {/* Por debajo de `lg` la barra de pestañas ocupa su propia línea a lo ancho
              disponible y se desliza en horizontal: la pestaña cortada contra el borde
              derecho es la señal de que hay más. Va sin márgenes negativos a propósito
              —como flex item con `-mx-4` la caja no crece parejo de los dos lados y las
              pestañas quedaban descentradas—; alineada al contenido de la tarjeta, como
              el buscador y las filas. `min-w-0` la deja encogerse en vez de empujar la
              tarjeta a lo ancho de las 5 pestañas. */}
          <div
            role="tablist"
            aria-label={t('Colas de trabajo')}
            className="flex items-center gap-1 overflow-x-auto w-full min-w-0 lg:w-auto"
          >
            {TABS.map((tb) => (
              <button
                key={tb.key}
                role="tab"
                aria-selected={tab === tb.key}
                onClick={() => setTab(tb.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-3 lg:py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-t',
                  tab === tb.key
                    ? 'border-brand-500 text-brand-700'
                    : 'border-transparent text-fg-muted hover:text-fg',
                )}
              >
                {tb.label}
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[1.25rem] h-[1.125rem] px-1 rounded-full text-[11px] tabular-nums',
                    tab === tb.key ? 'bg-brand-100 text-brand-800' : 'bg-bg-subtle text-fg-muted',
                  )}
                >
                  {tb.count}
                </span>
              </button>
            ))}
          </div>
          <div className="relative ml-auto py-1.5 w-full lg:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Buscar repartidor…')}
              aria-label={t('Buscar repartidor')}
              className="h-8 w-full rounded-lg border border-border bg-bg pl-8 pr-2.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        {loading ? (
          <>
            {/* El esqueleto lleva la forma del layout real: bajo `lg` la lista son
                tarjetas apiladas, y una tabla de 4 columnas se recortaba contra el
                borde de la tarjeta en mobile. */}
            <div className="lg:hidden divide-y divide-border" role="status" aria-busy="true">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-2 px-4 py-3.5">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
            <TableSkeleton rows={6} columns={4} className="border-0 hidden lg:block" />
          </>
        ) : visible.length === 0 ? (
          search ? (
            <EmptyState
              icon={<Search />}
              title={t('Sin resultados')}
              description={t('Nadie coincide con la búsqueda en esta pestaña.')}
              action={<Button variant="outline" onClick={() => setSearch('')}>{t('Limpiar búsqueda')}</Button>}
            />
          ) : tab === 'validar' ? (
            <EmptyState
              icon={<CheckCircle />}
              title={t('No hay documentos por validar')}
              description={t('Cuando un repartidor suba su factura o boleta, aparecerá acá para aceptarla o rechazarla.')}
            />
          ) : tab === 'pagar' ? (
            <EmptyState
              icon={<Banknote />}
              title={t('Nadie listo para pagar')}
              description={t('Un repartidor queda listo cuando su documento está aceptado y sus datos bancarios completos.')}
            />
          ) : tab === 'observacion' ? (
            <EmptyState
              icon={<CheckCircle />}
              title={t('Sin observaciones')}
              description={t('Todos los repartidores tienen su ficha y su documento en orden.')}
            />
          ) : (
            <EmptyState
              icon={<PackageCheck />}
              title={t('Sin repartidores en esta vista')}
              description={t('Cambia de pestaña o de período para ver movimientos.')}
            />
          )
        ) : (
          <div className="overflow-x-auto">
            {/* La grilla de 5 columnas necesita ~760px, así que el corte es por
                contenido y no por dispositivo: bajo `lg` (mobile y tablet vertical) la
                fila se apila como tarjeta, y el ancho mínimo con el scroll horizontal
                de la tabla solo aplican de `lg` hacia arriba. */}
            <ul className="divide-y divide-border lg:min-w-[760px] list-none m-0 p-0">
              {visible.map((r) => (
                <li key={r.d.driverId}>
                  <button
                    type="button"
                    onClick={() => {
                      setFichaId(r.d.driverId);
                      setUploadType(r.d.billingDocType ?? 'boleta');
                    }}
                    className={cn(
                      // Bajo `lg`: una columna apilada por repartidor (identidad →
                      // estado → avance → plata). Desde `lg`: la grilla de 5 columnas
                      // de la tabla, intacta.
                      'relative grid w-full items-center gap-x-4 gap-y-1.5 px-4 py-3.5 lg:py-3 text-left transition-colors',
                      'grid-cols-1 lg:grid-cols-[minmax(180px,2fr)_minmax(150px,1.3fr)_minmax(140px,1.2fr)_minmax(96px,0.9fr)_20px]',
                      'hover:bg-bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
                    )}
                  >
                    {/* Identidad: en mobile deja aire a la derecha para el chevron. */}
                    <span className="flex items-center gap-3 min-w-0 pr-6 lg:pr-0">
                      <Avatar
                        initials={initialsOf(r.name)}
                        size="sm"
                        tone={r.queue === 'pagado' ? 'neutral' : 'brand'}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-fg truncate" title={r.name}>
                          {r.name}
                        </span>
                        <span className="block text-xs text-fg-muted truncate">
                          {r.d.vehicleType ? driverVehicleLabel(r.d.vehicleType, t) : t('Sin vehículo')}
                          {' · '}
                          {r.d.isFreelancer
                            ? t('Freelancer')
                            : r.d.billingDocType
                              ? billingDocLabel(r.d.billingDocType, t)
                              : t('Sin tipo de documento')}
                          {/* Lidera una flota tercerizada: su monto incluye las entregas
                              de sus repartidores, que por eso no tienen fila propia. */}
                          {r.d.fleet && (
                            <>
                              {' · '}
                              {t('Flota de {{count}}', { count: r.d.fleet.members.length })}
                            </>
                          )}
                        </span>
                      </span>
                    </span>

                    {/* Estado del documento: apilado en desktop, en línea en mobile
                        (badge + conciliación caben juntos y ocupan menos alto). */}
                    <span className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1 min-w-0 lg:flex-col lg:items-start lg:gap-1">
                      <DocBadge d={r.d} weekOpen={weekOpen} t={t} />
                      <DocTypeMismatchBadge d={r.d} t={t} />
                      {r.delta !== null && r.d.billingDocStatus !== 'rejected' && (
                        r.delta === 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
                            <Check className="w-3 h-3 text-status-success" aria-hidden />
                            {t('N°{{folio}} cuadra exacto', { folio: fichaFolio(r.d) })}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-status-error">
                            <AlertTriangle className="w-3 h-3" aria-hidden />
                            {t('Difiere {{monto}}', { monto: formatCurrency(Math.abs(r.delta)) })}
                          </span>
                        )
                      )}
                      {r.queue === 'observacion' && !weekOpen && r.blockers[0] && r.delta === null && (
                        <span className="text-xs text-fg-muted truncate max-w-full" title={r.blockers.join(' · ')}>
                          {r.blockers[0]}
                        </span>
                      )}
                    </span>

                    <PayProgress
                      d={r.d}
                      pending={r.pending}
                      t={t}
                      className="flex-row flex-wrap items-center gap-x-3 gap-y-0.5 lg:flex-col lg:items-start lg:gap-1"
                    />

                    {/* Plata: en mobile es la última línea de la tarjeta, con el monto
                        adelante y el detalle en la misma corrida (mismo idioma que la
                        cinta del período); en desktop vuelve al bloque apilado a la
                        derecha de la tabla. */}
                    <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 lg:flex-col lg:items-end lg:gap-0 lg:text-right">
                      <span className="text-base font-semibold text-fg tabular-nums lg:text-sm">
                        {formatCurrency(r.d.totalPayable)}
                      </span>
                      <span className="text-xs text-fg-muted tabular-nums">
                        {t('{{n}} entregas', { n: r.d.deliveriesCount })}
                      </span>
                      <OversizedLine n={r.d.oversizedCount} t={t} />
                      <PickupsLine n={r.d.pickupsCount} t={t} />
                      {r.d.lastPaymentDate && (
                        <span
                          className="block text-xs text-fg-muted tabular-nums whitespace-nowrap"
                          title={(r.d.paymentDates?.length ?? 0) > 1
                            ? r.d.paymentDates.map(dayLabel).join(' · ')
                            : undefined}
                        >
                          {t('Últ. pago {{date}}', { date: dayLabel(r.d.lastPaymentDate) })}
                        </span>
                      )}
                    </span>

                    {/* Chevron: en mobile flota arriba a la derecha (la tarjeta entera es
                        el blanco táctil); en desktop vuelve a su columna de la grilla. */}
                    <ChevronRight
                      className="absolute right-3 top-4 w-4 h-4 text-fg-subtle lg:static lg:right-auto lg:top-auto lg:justify-self-end"
                      aria-hidden
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ===== Ficha de liquidación =====
          El pie se apila en mobile (fecha y botón a lo ancho): el pendiente, la fecha y
          la acción no caben en una fila de 390px y se desbordaban del panel. */}
      <Modal
        open={!!ficha}
        onClose={() => setFichaId(null)}
        size="lg"
        title={ficha?.name}
        description={ficha
          ? `${ficha.d.vehicleType ? driverVehicleLabel(ficha.d.vehicleType, t) : t('Sin vehículo')} · ${weekLabel(shownWeek.from, shownWeek.to)}`
          : undefined}
        footer={ficha && ficha.pending > 0 ? (
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <span className="text-sm text-fg-muted sm:mr-auto">
              {t('Pendiente:')}{' '}
              <span className="font-semibold text-status-warning tabular-nums">
                {formatCurrency(ficha.pending)}
              </span>
            </span>
            <span className="bill-field">
              <label className="bill-label" htmlFor="ficha-pay-date">{t('Fecha de pago')}</label>
              <Input
                id="ficha-pay-date"
                type="date"
                size="sm"
                value={fichaPayDate}
                onChange={(e) => setFichaPayDate(e.target.value)}
              />
            </span>
            <Button
              iconLeft={<CheckCircle className="w-4 h-4" />}
              loading={bulkBusy}
              disabled={!fichaPayDate}
              onClick={() => markWeekPaid(ficha)}
            >
              {t('Marcar período pagado')}
            </Button>
          </div>
        ) : undefined}
      >
        {ficha && (
          <div className="flex flex-col gap-6">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-fg tabular-nums">
                  {formatCurrency(ficha.d.totalPayable)}
                </span>
                <span className="text-sm text-fg-muted">{t('a cobrar en el período')}</span>
              </div>
              <p className="mt-1 text-xs text-fg-muted tabular-nums">
                {/* totalNet ya incluye los retiros (billing los suma a la base del día antes
                    del IVA o la retención): Entregas es la resta y Entregas + Retiros cuadra
                    exacto con totalNet. Por eso la retención y el IVA de abajo no cambian. */}
                {t('Entregas')} {formatCurrency((ficha.d.totalNet ?? 0) - (ficha.d.pickupsNet ?? 0))}
                {Number(ficha.d.pickupsCount ?? 0) > 0 && (
                  <>
                    {' · '}{t('Retiros')} {formatCurrency(ficha.d.pickupsNet ?? 0)}
                    {Number(ficha.d.pickupsUnpricedCount ?? 0) > 0 && (
                      <span className="text-status-warning">
                        {' '}({t('{{n}} sin tarifa', { n: Number(ficha.d.pickupsUnpricedCount) })})
                      </span>
                    )}
                  </>
                )}
                {(ficha.d.extrasTotal ?? 0) !== 0 && (
                  <>
                    {' · '}{t('Extras')}{' '}
                    <span className={ficha.d.extrasTotal > 0 ? 'text-status-success' : 'text-status-error'}>
                      {ficha.d.extrasTotal > 0 ? '+' : ''}{formatCurrency(ficha.d.extrasTotal)}
                    </span>
                  </>
                )}
                {' · '}
                {/* Boleta: el backend deja taxAmount en 0 (no hay IVA) y la retención
                    vive implícita en totalPayable — se muestra derivada (bruto − pago).
                    Con semanas de tipos distintos el total mezcla IVA y retención: una sola
                    cifra derivada mentiría, así que solo se dice cómo se calculó. */}
                {ficha.d.mixedDocTypes === true
                  ? t('IVA o retención según el tipo de cada semana')
                  : ficha.d.billingDocType === 'boleta'
                  ? `${t('Retención SII')} −${formatCurrency(
                      (ficha.d.totalNet ?? 0) + (ficha.d.extrasTotal ?? 0) - (ficha.d.totalPayable ?? 0),
                    )}`
                  : (ficha.d.taxAmount ?? 0) >= 0
                    ? `${t('IVA')} ${formatCurrency(ficha.d.taxAmount)}`
                    : `${t('Retención SII')} −${formatCurrency(Math.abs(ficha.d.taxAmount))}`}
              </p>
              {docTypeMismatchLabel(ficha.d, t) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <DocTypeMismatchBadge d={ficha.d} t={t} />
                  <span className="text-xs text-fg-muted">{docTypeMismatchLabel(ficha.d, t).title}</span>
                </div>
              )}
              {ficha.d.fleet && (
                <FleetBreakdown
                  fleet={ficha.d.fleet}
                  pickupsCount={Number(ficha.d.pickupsCount ?? 0)}
                  t={t}
                />
              )}
              {ficha.blockers.length > 0 && ficha.pending > 0 && (
                <div className="mt-3 flex flex-col gap-1.5">
                  {ficha.blockers.map((b) => (
                    <span key={b} className="inline-flex items-center gap-2 text-sm text-status-warning">
                      <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
                      {b}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Documento tributario (uno por semana del rango) */}
            {!ficha.d.isFreelancer && !weekOpen && (
              <div>
                <h3 className="text-sm font-semibold text-fg mb-2">{t('Documento tributario')}</h3>
                {fichaDocs.length === 0 ? (
                  <p className="text-sm text-fg-muted m-0">
                    {t('Todavía no sube su {{doc}} del período.', {
                      doc: ficha.d.billingDocType ? billingDocLabel(ficha.d.billingDocType, t) : t('factura o boleta'),
                    })}
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {fichaDocs.map((doc) => {
                      // Trazabilidad de la subida: la fecha ORIGINAL (firstUploadedAt) manda;
                      // uploadedAt cambia con cada reemplazo. Fallback mientras el backend
                      // viejo no exponga firstUploadedAt.
                      const ts = doc.firstUploadedAt ?? doc.uploadedAt;
                      const uploadedLabel = dateTimeLabel(ts);
                      const lateDays = daysAfterClose(ts, doc.weekEnd);
                      const replacedLabel =
                        doc.replacedCount > 0 && doc.firstUploadedAt && doc.firstUploadedAt !== doc.uploadedAt
                          ? dateTimeLabel(doc.uploadedAt)
                          : null;
                      return (
                      <div key={doc.weekStart} className="rounded-lg border border-border p-3.5 flex flex-col gap-2.5">
                        <div className="flex items-start justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => setViewerDoc({ doc, driverName: ficha.name })}
                            className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:underline min-w-0"
                            title={t('Ver el PDF')}
                          >
                            <FileText className="w-4 h-4 shrink-0" aria-hidden />
                            <span className="truncate">{doc.filename}</span>
                          </button>
                          <span className="shrink-0">
                            {doc.status === 'accepted' && (
                              doc.acceptedVia === 'ai'
                                ? <Badge tone="info">{t('Aceptado con IA')}</Badge>
                                : <Badge tone="success" dot>{t('Aceptado')}</Badge>
                            )}
                            {doc.status === 'uploaded' && <Badge tone="info" dot>{t('Por validar')}</Badge>}
                            {doc.status === 'rejected' && <Badge tone="error" dot>{t('Rechazado')}</Badge>}
                          </span>
                        </div>
                        {fichaDocs.length > 1 && (
                          <span className="text-xs text-fg-muted">
                            {t('Semana {{from}} al {{to}}', { from: dayLabel(doc.weekStart), to: dayLabel(doc.weekEnd) })}
                          </span>
                        )}
                        {uploadedLabel && (
                          <span className="text-xs text-fg-muted">
                            {t('Subido el {{fecha}}', { fecha: uploadedLabel })}
                            {lateDays !== null && lateDays >= 1 && (
                              <>
                                {' · '}
                                {/* >7 días tras el cierre es atraso inequívoco (el mínimo normal es 1). */}
                                <span className={lateDays > 7 ? 'font-medium text-status-error' : undefined}>
                                  {t('{{n}} día(s) después del cierre', { n: lateDays })}
                                </span>
                              </>
                            )}
                          </span>
                        )}
                        {replacedLabel && (
                          <span className="text-xs text-fg-muted">
                            {t('Reemplazado el {{fecha}}', { fecha: replacedLabel })}
                          </span>
                        )}
                        {doc.docMeta && (
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs m-0">
                            <div className="flex justify-between gap-2">
                              <dt className="text-fg-muted">{t('Folio')}</dt>
                              <dd className="text-fg tabular-nums m-0">N°{doc.docMeta.folio ?? '—'} <CopyButton value={doc.docMeta.folio} label={t('Copiar folio')} copiedMessage={t('Folio copiado')} /></dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-fg-muted">{t('Emisor')}</dt>
                              <dd className="text-fg m-0 truncate" title={doc.docMeta.emisorNombre ?? doc.docMeta.emisorRut ?? ''}>
                                {doc.docMeta.emisorNombre ?? doc.docMeta.emisorRut ?? '—'}
                              </dd>
                            </div>
                            {doc.docMeta.montoTotal != null && (
                              <div className="flex justify-between gap-2">
                                <dt className="text-fg-muted">{t('Declarado')}</dt>
                                <dd className="text-fg tabular-nums m-0">{formatCurrency(doc.docMeta.montoTotal)}</dd>
                              </div>
                            )}
                            <div className="flex justify-between gap-2">
                              <dt className="text-fg-muted">{t('Calculado')}</dt>
                              {/* Total que debería decir el DOCUMENTO: en boleta son los
                                  honorarios brutos, no el líquido con retención. */}
                              <dd className="text-fg tabular-nums m-0">
                                {formatCurrency(docExpectedTotal(ficha.d, doc))}
                              </dd>
                            </div>
                          </dl>
                        )}
                        {ficha.delta !== null && doc.status !== 'rejected' && (
                          ficha.delta === 0 ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-status-success">
                              <Check className="w-3.5 h-3.5" aria-hidden />
                              {t('El documento cuadra exacto con lo calculado.')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-status-error">
                              <AlertTriangle className="w-3.5 h-3.5" aria-hidden />
                              {t('Difiere en {{monto}} de lo calculado ({{dir}}).', {
                                monto: formatCurrency(Math.abs(ficha.delta)),
                                dir: ficha.delta < 0 ? t('declaró menos') : t('declaró más'),
                              })}
                            </span>
                          )
                        )}
                        {doc.status === 'rejected' && doc.rejectionReason && (
                          <span className="text-xs text-status-error">
                            {t('Motivo del rechazo: {{reason}}', { reason: doc.rejectionReason })}
                          </span>
                        )}
                        {/* La IA ya lo miró y no lo aceptó: el primer motivo, para que el
                            operador sepa qué revisar antes de aceptar a mano. */}
                        {doc.status === 'uploaded' && Array.isArray(doc.aiValidation?.reasons) && doc.aiValidation.reasons[0] && (
                          <span className="text-xs text-fg-muted" title={doc.aiValidation.reasons.join(' · ')}>
                            {t('IA: {{reason}}', { reason: String(doc.aiValidation.reasons[0]) })}
                          </span>
                        )}
                        <div className="flex gap-2 pt-0.5">
                          {doc.status !== 'accepted' && doc.status !== 'rejected' && (
                            <Button
                              size="sm"
                              onClick={() => setAcceptTarget({
                                driverId: ficha.d.driverId,
                                driverName: ficha.name,
                                doc,
                                d: ficha.d,
                                // Esperado por tipo solo si el rango cubre la semana del
                                // documento y los retiros se leyeron; si no, null.
                                expected: docComparableInRange(doc, { from, to, pickupsUnavailable })
                                  ? docExpectedTotals(ficha.d, doc)
                                  : null,
                              })}
                            >
                              {t('Aceptar')}
                            </Button>
                          )}
                          {doc.status !== 'rejected' && (
                            <Button
                              size="sm"
                              variant={doc.status === 'accepted' ? 'ghost' : 'outline'}
                              onClick={() => {
                                setRejectReason('');
                                setRejectTarget({
                                  driverId: ficha.d.driverId,
                                  driverName: ficha.name,
                                  weekStart: doc.weekStart,
                                  weekEnd: doc.weekEnd,
                                });
                              }}
                            >
                              {doc.status === 'accepted' ? t('Revertir aceptación (rechazar)') : t('Rechazar')}
                            </Button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Subida ADMIN en nombre del repartidor: para el documento que existe
                    en papel pero el repartidor no puede subir (app, teléfono, licencia).
                    Reemplaza al de la semana si estaba rechazado. */}
                {(fichaDocs.length === 0 || fichaDocs.every((x) => x.status === 'rejected')) && (
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <select
                      value={uploadType}
                      onChange={(e) => setUploadType(e.target.value)}
                      aria-label={t('Tipo de documento a subir')}
                      className="h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="boleta">{t('Boleta')}</option>
                      <option value="factura">{t('Factura')}</option>
                    </select>
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept="application/pdf"
                      className="sr-only"
                      aria-label={t('Archivo PDF del documento')}
                      onChange={handleUploadFile}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      iconLeft={<Upload className="w-4 h-4" />}
                      loading={uploadBusy}
                      onClick={() => uploadInputRef.current?.click()}
                    >
                      {fichaDocs.length > 0 ? t('Subir reemplazo (PDF)') : t('Subir documento (PDF)')}
                    </Button>
                    <span className="text-xs text-fg-subtle">
                      {t('Queda a nombre del repartidor, en la semana {{w}}.', {
                        w: weekLabel(shownWeek.from, shownWeek.to),
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Días trabajados */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-semibold text-fg m-0">{t('Días trabajados')}</h3>
                <span className="text-xs text-fg-muted tabular-nums">
                  {t('{{p}}/{{n}} pagados', { p: ficha.d.paidDaysCount, n: ficha.d.daysCount })}
                </span>
              </div>
              <ul className="divide-y divide-border/70 border-y border-border/70 list-none m-0 p-0">
                {(ficha.d.days ?? []).map((day) => (
                  <li key={day.date} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="w-24 text-sm text-fg tabular-nums shrink-0 capitalize">{dayLabel(day.date)}</span>
                    <span className="flex flex-col text-xs text-fg-muted tabular-nums shrink-0">
                      <span>{t('{{n}} entregas', { n: day.deliveriesCount })}</span>
                      <OversizedLine n={day.oversizedCount} t={t} />
                      <PickupsLine n={day.pickupsCount} net={day.pickupsNet ?? 0} t={t} />
                    </span>
                    {(day.extrasTotal ?? 0) !== 0 && (
                      <span className={cn('text-xs tabular-nums shrink-0', day.extrasTotal > 0 ? 'text-status-success' : 'text-status-error')}>
                        {day.extrasTotal > 0 ? '+' : ''}{formatCurrency(day.extrasTotal)}
                      </span>
                    )}
                    <button
                      type="button"
                      className="text-xs font-medium text-brand-700 hover:underline shrink-0"
                      onClick={() => setDayModal({
                        driverId: ficha.d.driverId,
                        driverName: ficha.name,
                        date: day.date,
                        isFreelancer: !!ficha.d.isFreelancer,
                      })}
                    >
                      {t('Gestionar')}
                    </button>
                    <span className="ml-auto text-sm font-medium text-fg tabular-nums">
                      {formatCurrency(day.totalPayable)}
                    </span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <Badge tone={DAY_STATUS_TONE[day.status] ?? 'neutral'}>
                        {t(DAY_STATUS_LABEL[day.status] ?? 'Sin pagar')}
                      </Badge>
                      {day.paymentDate && editPayDay?.date !== day.date && (
                        <>
                          <span className="text-xs text-fg-muted tabular-nums">
                            {t('Pagado el {{date}}', { date: dayLabel(day.paymentDate) })}
                          </span>
                          <button
                            type="button"
                            className="text-fg-subtle hover:text-fg transition-colors"
                            aria-label={t('Editar fecha de pago')}
                            onClick={() => setEditPayDay({ date: day.date, value: day.paymentDate })}
                          >
                            <Pencil className="w-3.5 h-3.5" aria-hidden />
                          </button>
                        </>
                      )}
                      <Switch
                        checked={day.status !== 'unpaid'}
                        disabled={dayBusy === day.date}
                        onChange={() => toggleDayPaid(ficha.d.driverId, day.date, day.status !== 'unpaid')}
                        aria-label={t('Marcar {{date}} como pagado', { date: dayLabel(day.date) })}
                      />
                    </span>
                    {editPayDay?.date === day.date && (
                      // Edición retroactiva de la fecha de pago: sub-línea propia para no
                      // desbordar la fila (addendum: flex-wrap + basis-full).
                      <span className="basis-full flex flex-wrap items-center gap-2 pl-24">
                        <label className="bill-label m-0" htmlFor={`edit-pay-date-${day.date}`}>
                          {t('Fecha de pago')}
                        </label>
                        <Input
                          id={`edit-pay-date-${day.date}`}
                          type="date"
                          size="sm"
                          className="max-w-[160px]"
                          value={editPayDay.value}
                          onChange={(e) => setEditPayDay({ date: day.date, value: e.target.value })}
                        />
                        <Button size="sm" loading={editPayBusy} onClick={saveDayPaymentDate}>
                          {t('Guardar fecha')}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={editPayBusy} onClick={() => setEditPayDay(null)}>
                          {t('Cancelar')}
                        </Button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Datos bancarios (resumen del backend, misma regla que la planilla) */}
            {ficha.d.bankData !== undefined && !ficha.d.isFreelancer && (
              <div>
                <h3 className="text-sm font-semibold text-fg mb-2">{t('Datos bancarios')}</h3>
                {ficha.d.bankData === null ? (
                  <p className="inline-flex items-center gap-2 text-sm text-status-warning m-0">
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    {t('Sin datos bancarios: no puede entrar a la planilla del banco.')}
                  </p>
                ) : (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-bg-muted text-fg-muted shrink-0">
                      <Landmark className="w-4 h-4" aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <div className="text-fg">
                        {[ficha.d.bankData.bank, ficha.d.bankData.accountType].filter(Boolean).join(' · ')}{' '}
                        <span className="tabular-nums">{ficha.d.bankData.accountNumber ?? ''}</span>
                      </div>
                      <div className="text-xs text-fg-muted">
                        {t('Titular {{name}} · {{rut}}', {
                          name: ficha.d.bankData.holder ?? '—',
                          rut: ficha.d.bankData.holderRut ?? '—',
                        })}
                      </div>
                      {!ficha.d.bankData.complete && ficha.d.bankData.missingReason && (
                        <div className="text-xs text-status-warning mt-0.5">{ficha.d.bankData.missingReason}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ===== Asistente "Pagar semana" ===== */}
      <Modal
        open={payOpen}
        onClose={() => {
          if (payBusy) return;
          setPayOpen(false);
          // El paso de banco trae las filas YA filtradas (documento aceptado + cuenta del
          // emisor): se descartan al cerrar, para no reabrirlo con una autorización vieja.
          setBankPick(null);
        }}
        size="md"
        title={t('Pagar semana')}
        description={weekLabel(shownWeek.from, shownWeek.to)}
        footer={payDone ? (
          <Button onClick={() => setPayOpen(false)}>{t('Listo')}</Button>
        ) : bankPick ? (
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setBankPick(null)}>
              {t('Volver')}
            </Button>
            <Button
              iconLeft={<Download className="w-4 h-4" />}
              disabled={!formatForBank(selectedBank)}
              onClick={downloadPickedBank}
            >
              {t('Descargar')}
            </Button>
          </div>
        ) : (
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              iconLeft={<Download className="w-4 h-4" />}
              loading={bankBusy}
              onClick={handleBankExport}
            >
              {t('Descargar planilla banco')}
            </Button>
            <Button
              iconLeft={<CheckCircle className="w-4 h-4" />}
              loading={payBusy}
              disabled={byQueue.pagar.length === 0 || !payDate}
              onClick={confirmWeekPay}
            >
              {t('Confirmar pago a {{n}}', { n: byQueue.pagar.length })}
            </Button>
          </div>
        )}
      >
        {payDone ? (
          <div className="flex flex-col items-center text-center py-8 gap-3">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-status-success-soft text-status-success">
              <CheckCircle className="w-6 h-6" aria-hidden />
            </span>
            <p className="text-base font-semibold text-fg m-0">{t('Pago registrado')}</p>
            <p className="text-sm text-fg-muted max-w-sm m-0">
              {t('{{d}} días de {{n}} repartidores quedaron marcados como pagados. Puedes revisar o revertir cualquier día desde su ficha.', {
                d: payDone.updated,
                n: payDone.drivers,
              })}
            </p>
          </div>
        ) : bankPick ? (
          <div className="flex flex-col gap-3">
            <div className="bill-field max-w-[300px]">
              <label className="bill-label" htmlFor="pay-bank">{t('Banco')}</label>
              <Select
                id="pay-bank"
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
                placeholder={t('Seleccionar banco')}
              >
                {CHILE_BANKS.map((b) => {
                  const has = SUPPORTED_BANK_CODES.includes(b.code);
                  return (
                    <option key={b.code} value={b.code} disabled={!has}>
                      {has ? b.name : `${b.name} (${t('formato no disponible')})`}
                    </option>
                  );
                })}
              </Select>
              {!formatForBank(selectedBank) && (
                <span className="text-xs text-status-warning mt-1">
                  {t('Este banco aún no tiene formato de planilla. Elige otro.')}
                </span>
              )}
            </div>
            <p className="text-sm text-fg-muted m-0">
              {t('La planilla se genera con el formato de pago masivo del banco elegido.')}
            </p>
            <p className="text-sm text-fg m-0">
              {t('Se transferirán {{monto}} a {{n}} repartidores.', {
                monto: formatCurrency(bankPickTotal),
                n: bankPick.rows.length,
              })}
            </p>
            {byQueue.pagar.length > bankPick.rows.length && (
              <p className="text-xs text-fg-muted m-0">
                {t('{{n}} de los elegibles no entran a la planilla.', {
                  n: byQueue.pagar.length - bankPick.rows.length,
                })}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <p className="text-sm text-fg-muted m-0">
              {issuerMustMatch
                ? t('Se pagará a quienes tienen su documento aceptado, datos bancarios completos y la cuenta a nombre del emisor del documento. Descarga la planilla, ejecútala en el banco y confirma para dejar el registro.')
                : t('Se pagará a quienes tienen su documento aceptado y datos bancarios completos. Descarga la planilla, ejecútala en el banco y confirma para dejar el registro.')}
            </p>

            <div className="bill-field max-w-[240px]">
              <label className="bill-label" htmlFor="pay-week-date">{t('Fecha de emisión del pago')}</label>
              <Input
                id="pay-week-date"
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
              />
              <span className="text-xs text-fg-subtle mt-1">
                {t('Quedará registrada como la fecha de pago de todos los días incluidos.')}
              </span>
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-semibold text-fg m-0">
                  {t('Elegibles ({{n}})', { n: byQueue.pagar.length })}
                </h3>
                <span className="text-sm font-semibold text-fg tabular-nums">{formatCurrency(readyAmount)}</span>
              </div>
              {byQueue.pagar.length === 0 ? (
                <p className="text-sm text-fg-muted m-0">{t('Nadie cumple los requisitos todavía.')}</p>
              ) : (
                <ul className="divide-y divide-border/70 border-y border-border/70 list-none m-0 p-0 max-h-64 overflow-auto">
                  {byQueue.pagar.map((r) => (
                    <li key={r.d.driverId} className="flex items-center gap-3 py-2">
                      <Avatar initials={initialsOf(r.name)} size="xs" />
                      <span className="text-sm text-fg truncate">{r.name}</span>
                      {r.d.isFreelancer && (
                        <Badge tone="neutral" title={t('La planilla banco no incluye freelancers: se les paga por otro canal.')}>
                          {t('Freelancer · fuera de planilla')}
                        </Badge>
                      )}
                      <span className="ml-auto text-sm font-medium text-fg tabular-nums">
                        {formatCurrency(r.pending)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {(byQueue.validar.length > 0 || byQueue.observacion.length > 0) && (
              <div>
                <h3 className="text-sm font-semibold text-fg mb-2">
                  {t('Quedan fuera ({{n}})', { n: byQueue.validar.length + byQueue.observacion.length })}
                </h3>
                <ul className="flex flex-col gap-1.5 list-none m-0 p-0 max-h-48 overflow-auto">
                  {[...byQueue.validar, ...byQueue.observacion].map((r) => (
                    <li key={r.d.driverId} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" aria-hidden />
                      <span className="min-w-0">
                        <span className="text-fg">{r.name}</span>
                        <span className="text-fg-muted"> — {r.blockers[0] ?? t('Documento pendiente de validar')}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ===== Rango personalizado ===== */}
      <Modal
        open={rangeOpen}
        onClose={() => setRangeOpen(false)}
        size="sm"
        title={t('Rango personalizado')}
        description={t('Consulta un rango libre (para auditar más de una semana). La liquidación normal se navega por semanas.')}
        footer={(
          <>
            <Button variant="outline" onClick={() => setRangeOpen(false)}>{t('Cancelar')}</Button>
            <Button
              iconLeft={<Search className="w-4 h-4" />}
              onClick={() => {
                if (draftTo < draftFrom) {
                  toast.warning(t('La fecha "hasta" no puede ser anterior a "desde"'));
                  return;
                }
                setRangeOpen(false);
                applyRange({ from: draftFrom, to: draftTo });
              }}
            >
              {t('Buscar')}
            </Button>
          </>
        )}
      >
        <div className="flex gap-3">
          <Input
            label={t('Desde')}
            type="date"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
          />
          <Input
            label={t('Hasta')}
            type="date"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
          />
        </div>
      </Modal>

      {/* ===== Lotes de extras ===== */}
      <Modal
        open={lotesOpen}
        onClose={() => setLotesOpen(false)}
        size="lg"
        title={t('Lotes de extras')}
        description={t('Cada carga masiva queda registrada con su detalle descargable; un lote se puede revertir completo.')}
      >
        {batchesLoading ? (
          <div className="grid gap-3 sm:grid-cols-2" role="status" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-border p-3 flex flex-col gap-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-8 w-1/2" />
              </div>
            ))}
          </div>
        ) : !batches || batches.length === 0 ? (
          <EmptyState
            icon={<FileSpreadsheet />}
            title={t('Aún no hay lotes de extras cargados.')}
            description={t('Los archivos que subas con "Carga masiva de extras" quedarán registrados acá, con su detalle descargable.')}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {batches.map((b) => {
              const rolled = b.status === 'rolled_back';
              return (
                <div key={b.batchId} className="rounded-lg border border-border p-3.5 flex flex-col gap-2 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg-muted text-fg-muted shrink-0">
                        <FileSpreadsheet className="w-4 h-4" aria-hidden />
                      </span>
                      <span
                        className={cn('text-sm font-medium text-fg truncate', rolled && 'line-through text-fg-muted')}
                        title={b.filename || b.batchId}
                      >
                        {b.filename || b.batchId}
                      </span>
                    </div>
                    <Badge tone={rolled ? 'neutral' : 'success'} className="shrink-0">
                      {rolled ? t('Revertido') : t('Aplicado')}
                    </Badge>
                  </div>
                  <div className="text-xs text-fg-muted tabular-nums">
                    {b.createdAt ? String(b.createdAt).slice(0, 10) : ''}
                    {' · '}{t('{{n}} extras', { n: b.appliedCount })}
                    {' · '}<span className="font-semibold text-fg">{formatCurrency(b.totalAmount)}</span>
                  </div>
                  {b.uploadedByName && (
                    <div className="flex items-center gap-1.5 text-xs text-fg-muted min-w-0">
                      <UserRound className="w-3.5 h-3.5 shrink-0" aria-hidden />
                      <span className="truncate" title={b.uploadedByName}>
                        {t('Subido por {{name}}', { name: b.uploadedByName })}
                      </span>
                    </div>
                  )}
                  {rolled && b.rolledBackByName && (
                    <div className="flex items-center gap-1.5 text-xs text-fg-muted min-w-0">
                      <RotateCcw className="w-3.5 h-3.5 shrink-0" aria-hidden />
                      <span className="truncate">
                        {t('Revertido por {{name}}{{date}}', {
                          name: b.rolledBackByName,
                          date: b.rolledBackAt ? ` · ${String(b.rolledBackAt).slice(0, 10)}` : '',
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 mt-auto pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      iconLeft={<Download className="w-3.5 h-3.5" />}
                      loading={batchDlBusy === b.batchId}
                      disabled={rolled}
                      title={rolled
                        ? t('El lote fue revertido: sus filas ya no existen.')
                        : t('Descargar el detalle del lote en Excel')}
                      onClick={() => downloadBatch(b)}
                    >
                      {t('Excel')}
                    </Button>
                    {!rolled && (
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft={<RotateCcw className="w-3.5 h-3.5" />}
                        loading={rollbackBusy === b.batchId}
                        onClick={() => rollback(b.batchId)}
                      >
                        {t('Revertir')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* ===== Validaciones con IA: historial de jobs (mismo patrón que los lotes) ===== */}
      <Modal
        open={aiHistoryOpen}
        onClose={() => setAiHistoryOpen(false)}
        size="lg"
        title={t('Validaciones con IA')}
        description={t('Cada corrida queda registrada con su detalle por documento; un lote se puede deshacer completo.')}
      >
        {aiJobsLoading && aiJobs === null ? (
          <div className="grid gap-3 sm:grid-cols-2" role="status" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-border p-3 flex flex-col gap-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-8 w-1/2" />
              </div>
            ))}
          </div>
        ) : !aiJobs || aiJobs.length === 0 ? (
          <EmptyState
            icon={<Sparkles />}
            title={t('Aún no hay validaciones con IA.')}
            description={t('Cuando valides la cola "Por validar" con IA, cada corrida quedará registrada acá.')}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {aiJobs.map((j) => {
              const c = j.counters ?? {};
              const isShadow = j.mode === 'shadow';
              const rolled = !!j.rolledBackAt;
              const running = j.status === 'queued' || j.status === 'running';
              const canUndo = !isShadow && !rolled && !running && Number(c.accepted ?? 0) > 0;
              const when = j.requestedAt ?? j.startedAt;
              return (
                <div key={j.jobId} className="rounded-lg border border-border p-3.5 flex flex-col gap-2 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg-muted text-fg-muted shrink-0">
                        <Sparkles className="w-4 h-4" aria-hidden />
                      </span>
                      <span className={cn('text-sm font-medium text-fg tabular-nums', rolled && 'line-through text-fg-muted')}>
                        {(when && dateTimeLabel(when)) || j.jobId}
                      </span>
                    </div>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {rolled ? (
                        <Badge tone="neutral">{t('Deshecho')}</Badge>
                      ) : running ? (
                        <Badge tone="info" dot>{t('En curso')}</Badge>
                      ) : j.status === 'failed' ? (
                        <Badge tone="error" dot>{t('Con error')}</Badge>
                      ) : (
                        <Badge tone={isShadow ? 'info' : 'success'}>{isShadow ? t('Sombra') : t('Activo')}</Badge>
                      )}
                    </span>
                  </div>
                  <div className="text-xs text-fg-muted tabular-nums">
                    {isShadow
                      ? t('{{n}} aceptaría', { n: Number(c.wouldAccept ?? 0) })
                      : t('{{n}} aceptados', { n: Number(c.accepted ?? 0) })}
                    {' · '}{t('{{n}} retenidos', { n: Number(c.held ?? 0) })}
                    {' · '}{t('{{n}} para humano', { n: Number(c.needsHuman ?? 0) })}
                    {Number(c.ocr ?? 0) > 0 && <>{' · '}{t('OCR: {{n}}', { n: Number(c.ocr) })}</>}
                    {' · '}<span className="text-fg">{t('{{n}} en total', { n: Number(c.total ?? 0) })}</span>
                  </div>
                  {j.requestedByName && (
                    <div className="flex items-center gap-1.5 text-xs text-fg-muted min-w-0">
                      <UserRound className="w-3.5 h-3.5 shrink-0" aria-hidden />
                      <span className="truncate" title={j.requestedByName}>
                        {t('Solicitada por {{name}}', { name: j.requestedByName })}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 mt-auto pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setAiHistoryOpen(false);
                        setAiModal({ stage: running ? 'progress' : 'result', jobId: j.jobId });
                      }}
                    >
                      {running ? t('Ver avance') : t('Ver detalle')}
                    </Button>
                    {canUndo && (
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft={<RotateCcw className="w-3.5 h-3.5" />}
                        loading={aiRollbackBusy === j.jobId}
                        onClick={() => rollbackAi(j)}
                      >
                        {t('Deshacer lote')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {aiModal && (
        <AiValidationModal
          open
          stage={aiModal.stage}
          jobId={aiModal.jobId ?? null}
          items={aiItems}
          mode={aiStatus?.mode ?? 'active'}
          scans={aiStatus?.scans ?? null}
          drivers={allDrivers}
          onClose={onAiModalClose}
          onFinished={onAiFinished}
        />
      )}

      {/* ===== Modales reutilizados del módulo ===== */}
      {dayModal && (
        <DayPaymentModal
          open
          driverId={dayModal.driverId}
          driverName={dayModal.driverName}
          date={dayModal.date}
          isFreelancer={dayModal.isFreelancer}
          tz={getTz()}
          onClose={() => setDayModal(null)}
          onChanged={refetch}
        />
      )}

      <BulkExtrasModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onApplied={() => {
          refetch();
          if (lotesOpen) loadBatches();
          else setBatches(null);
        }}
      />

      <DocumentViewerModal
        open={!!viewerDoc}
        url={viewerDoc?.doc?.url}
        filename={viewerDoc?.doc?.filename}
        title={viewerDoc?.doc?.filename || t('Documento')}
        description={
          viewerDoc?.doc
            ? t('{{name}} · semana {{from}} al {{to}}', {
                name: viewerDoc.driverName || '',
                from: formatDate(viewerDoc.doc.weekStart),
                to: formatDate(viewerDoc.doc.weekEnd),
              })
            : undefined
        }
        onClose={() => setViewerDoc(null)}
      />

      <AcceptDocumentModal
        open={!!acceptTarget}
        target={acceptTarget}
        onClose={() => setAcceptTarget(null)}
        onAccepted={refetch}
      />

      {/* Rechazo con motivo obligatorio (es lo único que el repartidor va a leer). */}
      <Modal
        open={!!rejectTarget}
        onClose={() => { if (!rejectBusy) setRejectTarget(null); }}
        size="md"
        title={t('Rechazar documento')}
        description={t('{{name}} · semana {{from}} al {{to}}. El PDF no se borra: queda marcado como rechazado y el repartidor podrá subir otro.', {
          name: rejectTarget?.driverName ?? '',
          from: dayLabel(rejectTarget?.weekStart ?? ''),
          to: dayLabel(rejectTarget?.weekEnd ?? ''),
        })}
        footer={(
          <>
            <Button variant="outline" onClick={() => setRejectTarget(null)} disabled={rejectBusy}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleRejectDoc} loading={rejectBusy} disabled={!rejectReason.trim()}>
              {t('Rechazar y avisar')}
            </Button>
          </>
        )}
      >
        <div className="bill-field">
          <label className="bill-label" htmlFor="reject-doc-reason">{t('Motivo')}</label>
          <Input
            id="reject-doc-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('Ej: el monto no coincide con la semana')}
            maxLength={500}
          />
        </div>
      </Modal>

      {/* Excluidos de la planilla por datos bancarios incompletos. */}
      <Modal
        open={!!missing && missing.length > 0}
        onClose={() => setMissing(null)}
        size="md"
        title={t('Repartidores excluidos de la planilla')}
        description={t('Tienen monto por pagar pero les faltan datos bancarios. Complétalos para incluirlos en la próxima planilla.')}
        footer={<Button onClick={() => setMissing(null)}>{t('Entendido')}</Button>}
      >
        <div className="flex flex-col gap-2">
          {(missing ?? []).map((m, i) => (
            <div key={m.driverId || i} className="flex items-start gap-2 py-2 border-t border-border/60 first:border-t-0">
              <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <div className="text-sm text-fg">{m.driverName || m.driverId}</div>
                {m.reason && <div className="text-xs text-fg-muted">{m.reason}</div>}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Confirmación de la notificación (dry-run → envío real). */}
      <Modal
        open={!!notifyPreview}
        onClose={() => { if (!notifySending) setNotifyPreview(null); }}
        size="md"
        title={t('Notificar monto a pago')}
        description={t('Se enviará un correo a {{n}} repartidor(es) con su monto pendiente del período {{from}} al {{to}}.', {
          n: notifyPreview?.recipients?.length ?? 0,
          from: dayLabel(from),
          to: dayLabel(to),
        })}
        footer={(
          <>
            <Button variant="outline" onClick={() => setNotifyPreview(null)} disabled={notifySending}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleNotifySend} loading={notifySending} iconLeft={<Mail className="w-4 h-4" />}>
              {t('Enviar {{n}} correos', { n: notifyPreview?.recipients?.length ?? 0 })}
            </Button>
          </>
        )}
      >
        <div className="flex flex-col gap-2 max-h-80 overflow-auto">
          {(notifyPreview?.recipients ?? []).map((r, i) => (
            <div key={r.driverId || i} className="flex items-start justify-between gap-3 py-2 border-t border-border/60 first:border-t-0">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <span className="text-sm text-fg truncate">{r.driverName || r.driverId}</span>
                  {r.mixedDocTypes === true ? (
                    <Badge tone="neutral" className="shrink-0">{t('Semanas con tipos distintos')}</Badge>
                  ) : r.billingDocType ? (
                    <Badge tone="neutral" className="shrink-0">{billingDocLabel(r.billingDocType, t)}</Badge>
                  ) : (
                    <Badge
                      tone="warning"
                      className="shrink-0"
                      title={t('Recibirá ambos montos (factura y boleta) hasta que elija documento.')}
                    >
                      {t('Sin documento')}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-fg-muted truncate">{r.email}</div>
              </div>
              <span className="text-sm font-medium text-fg tabular-nums text-right shrink-0">{recipientAmount(r)}</span>
            </div>
          ))}
        </div>
      </Modal>

      {/* Correos que fallaron en el envío real. */}
      <Modal
        open={!!notifyFailed && notifyFailed.length > 0}
        onClose={() => setNotifyFailed(null)}
        size="md"
        title={t('No se pudieron enviar algunos correos')}
        description={t('Estos repartidores no recibieron el correo. Revisa el motivo y reintenta.')}
        footer={<Button onClick={() => setNotifyFailed(null)}>{t('Entendido')}</Button>}
      >
        <div className="flex flex-col gap-2">
          {(notifyFailed ?? []).map((f, i) => (
            <div key={f.driverId || i} className="flex items-start gap-2 py-2 border-t border-border/60 first:border-t-0">
              <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <div className="text-sm text-fg">{f.driverName || f.driverId}</div>
                {f.reason && <div className="text-xs text-fg-muted">{f.reason}</div>}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Spinner sutil del menú (notify dry-run / export en curso) */}
      {(notifyBusy || exportBusy) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] rounded-lg bg-fg text-bg text-sm px-4 py-2 shadow-elevated">
          {notifyBusy ? t('Preparando notificación…') : t('Generando resumen…')}
        </div>
      )}
    </div>
  );
};

// Folio del único documento del período (para la línea de conciliación de la fila).
const fichaFolio = (d) => d.billingDocuments?.[0]?.docMeta?.folio ?? '—';

/**
 * Desglose de una FLOTA TERCERIZADA: cuántas entregas puso cada repartidor asociado y
 * cuánto de la cifra del líder le corresponde.
 *
 * Existe porque el total del líder es, en general, inexplicable mirando solo sus propias
 * entregas: incluye las de su grupo. Sin este detalle, la única forma de auditar por qué
 * se le paga X sería contar paquetes a mano. Las entregas propias del líder salen como
 * el resto (total − suma de los miembros), que es la resta que el operador va a hacer.
 */
function FleetBreakdown({ fleet, pickupsCount = 0, t }) {
  const [open, setOpen] = useState(false);
  const members = fleet.members ?? [];
  // El aporte de un miembro son sus entregas (`amount`) MÁS sus retiros (`pickupsAmount`,
  // misma semántica: neto sin IVA ni extras). Sin sumar los retiros, la resta "total −
  // aporte de la flota" le atribuiría al líder retiros que hicieron sus repartidores.
  const memberAmount = (m) => (m.amount ?? 0) + (m.pickupsAmount ?? 0);
  const membersTotal = members.reduce((acc, m) => acc + memberAmount(m), 0);
  const membersDeliveries = members.reduce((acc, m) => acc + (m.deliveriesCount ?? 0), 0);
  const membersPickups = members.reduce((acc, m) => acc + (m.pickupsCount ?? 0), 0);
  // `pickupsCount` es el del líder como PAGADOR: incluye los retiros de sus miembros. Los
  // propios son la diferencia; si todos los hizo la flota, el resto del total son solo
  // entregas y nombrar retiros ahí sería falso.
  const leaderHasOwnPickups = pickupsCount > membersPickups;

  return (
    <div className="mt-3 rounded-lg border border-border bg-bg-muted p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left text-sm font-medium text-fg"
      >
        <Users className="w-4 h-4 shrink-0" aria-hidden />
        {t('Flota tercerizada')}
        <span className="text-xs font-normal text-fg-muted">
          {t('{{count}} repartidores · {{monto}} por entrega', {
            count: members.length,
            monto: formatCurrency(fleet.amountPerDelivery),
          })}
        </span>
        <ChevronDown
          className={cn('ml-auto w-4 h-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        members.length === 0 ? (
          <p className="mt-2 mb-0 text-xs text-fg-muted">
            {t('Todavía no tiene repartidores asociados: cobra solo sus propias entregas, al monto de la flota.')}
          </p>
        ) : (
          <>
            <ul className="mt-2 list-none m-0 p-0 divide-y divide-border/60">
              {members.map((m) => (
                <li key={m.driverId} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="min-w-0 flex-1 truncate text-fg" title={m.driverName || m.driverId}>
                    {m.driverName || m.driverId}
                  </span>
                  <span className="shrink-0 text-xs text-fg-muted tabular-nums">
                    {t('{{count}} entregas', { count: m.deliveriesCount })}
                    {m.oversizedCount > 0 && ` · ${t('{{count}} XL', { count: m.oversizedCount })}`}
                    {m.pickupsCount > 0 && ` · ${t('{{n}} retiros', { n: m.pickupsCount })}`}
                  </span>
                  <span className="w-24 shrink-0 text-right text-sm text-fg tabular-nums">
                    {formatCurrency(memberAmount(m))}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 mb-0 text-xs text-fg-muted tabular-nums">
              {t('Aporte de la flota')}: {membersDeliveries} {t('entregas')}
              {membersPickups > 0 && ` · ${t('{{n}} retiros', { n: membersPickups })}`}
              {' · '}
              {formatCurrency(membersTotal)}.{' '}
              {leaderHasOwnPickups
                ? t('El resto del total son entregas y retiros propios del líder.')
                : t('El resto del total son entregas propias del líder.')}
            </p>
          </>
        )
      )}
    </div>
  );
}

export default DriverPaymentsSummary;
